
import React, { useState, useEffect, useRef, forwardRef } from "react";
import { createPortal } from "react-dom";
import { ShoppingItemWithProduct } from "@/types";
import TagDisplay from "./attributes/TagDisplay";
import { ProductAttribute } from "@/types/attributes";
import { enhancedImageCacheService } from "@/services/offline/image-cache-enhanced.service";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import ProductTileMenu from "./ProductTileMenu";

interface ShoppingListItemProps {
  item: ShoppingItemWithProduct;
  onToggle: (item: ShoppingItemWithProduct) => void;
  isFiltered?: boolean;
  onCheck?: (id: string, checked: boolean) => void;
  onDelete?: (id: string) => void;
  onEdit?: (item: ShoppingItemWithProduct) => void;
  rapidInsert?: boolean;
}

const ShoppingListItem = forwardRef<HTMLDivElement, ShoppingListItemProps>(
  ({ item, onToggle, isFiltered = false, onCheck, onDelete, onEdit, rapidInsert = false }, ref) => {
    const product = item.product || null;
  const [cachedImageUrl, setCachedImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  // Evita la riapertura immediata dopo la chiusura dell'anteprima
  const ignoreNextClick = useRef(false);
    const localRef = useRef<HTMLDivElement>(null);
    const { theme } = useThemeConfig();

    // Load cached image if available
    useEffect(() => {
      if (product?.photo && !imageError) {
        enhancedImageCacheService
          .getImageUrl(product.id, product.photo)
          .then(setCachedImageUrl)
          .catch(() => setImageError(true));
      }
    }, [product?.id, product?.photo, imageError]);

    const pressStartTime = useRef<number | null>(null);

    const handlePointerDown = (e: React.PointerEvent) => {
      if (showModal) return; // non interferire quando la modale è aperta
      e.preventDefault();
      pressStartTime.current = Date.now();
    };

    const handlePointerUp = (e: React.PointerEvent) => {
      if (showModal) return;
      e.preventDefault();
      const now = Date.now();
      const duration = pressStartTime.current ? now - pressStartTime.current : 0;

      if (duration >= 400) {
        onEdit?.(item);
      }

      pressStartTime.current = null;
    };

    const handleImageError = () => {
      setImageError(true);
      if (product) {
        enhancedImageCacheService.invalidateImage(product.id);
      }
      setCachedImageUrl(null);
    };

    const handleImageClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }
      setShowModal(true);
    };

    const handleImagePointerDown = (e: React.PointerEvent) => {
      e.stopPropagation();
    };

    const handleImagePointerUp = (e: React.PointerEvent) => {
      e.stopPropagation();
    };

    useEffect(() => {
      // Consente il menu contestuale al di fuori del singolo elemento
      const handleContextMenu = (e: MouseEvent) => {
        if (localRef.current && localRef.current.contains(e.target as Node)) {
          e.preventDefault();
        }
      };

      document.addEventListener("contextmenu", handleContextMenu);

      return () => {
        document.removeEventListener("contextmenu", handleContextMenu);
      };
    }, []);

    // Chiude l'anteprima con il tasto Esc
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowModal(false);
          ignoreNextClick.current = true;
        }
      };

      if (showModal) {
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [showModal]);

    // Chiude l'anteprima come con Esc e previene riaperture accidentali
    const closePreview = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowModal(false);
      ignoreNextClick.current = true;
    };

    const handleToggle = (e: React.MouseEvent) => {
      // Evita il toggle quando si clicca sull'immagine o sulla modale
      if (showModal) return;
      if ((e.target as HTMLElement).closest('.product-image-btn')) return;
      e.stopPropagation();
      onToggle(item);
      if (onCheck) onCheck(item.id, !item.checked);
    };

    const bgColor = item.checked ? theme.errorColor : theme.successColor;
    const textColor = item.checked ? theme.sectionTextColor : theme.textColor;
    const minHeight = !rapidInsert ? "min-h-[64px]" : "min-h-[84px]";

    const photoUrl = cachedImageUrl;
    
    // Enhanced name fallback with better prioritization
    const itemName =
      product?.name ||
      item.name ||
      item.product_id ||
      "Prodotto sconosciuto";
    const itemAttributes = Array.isArray(item.attributes) ? item.attributes : [];

    return (
      <div
        ref={(node) => {
          localRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        className={`tutorial-target-productcard relative rounded-md px-3 py-2 flex flex-col justify-between
          transition-all duration-150 cursor-pointer group
          ${minHeight} overflow-hidden select-none active:scale-95 transition-transform`}
        style={{
          backgroundColor: bgColor,
          color: theme.textPrimaryColor,
          textDecoration: item.checked ? 'line-through' : undefined,
        }}
      >
        {(onEdit || onDelete) && (
          <div className="absolute top-1 right-1 z-30">
            <ProductTileMenu
              onEdit={onEdit ? () => onEdit(item) : undefined}
              onDelete={onDelete ? () => onDelete(item.id) : undefined}
            />
          </div>
        )}
        {photoUrl && !imageError && (
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundColor: '#fff',
              opacity: 0.1,
              pointerEvents: 'none',
            }}
          />
        )}

        {photoUrl && !imageError && (
          // Miniatura in basso a destra: parte dal bordo e svanisce verso il centro
          <button
            type="button"
            aria-label="Apri foto prodotto"
            className="product-image-btn absolute bottom-0 right-0 z-10 w-[30px] h-[30px]"
            onClick={handleImageClick}
            onPointerDown={handleImagePointerDown}
            onPointerUp={handleImagePointerUp}
          >
            <img
              src={photoUrl}
              alt="Foto prodotto"
              className="w-full h-full object-cover"
              loading="lazy"
              onError={handleImageError}
            />
          </button>
        )}

        <div className="flex-1 flex flex-col items-left justify-left text-left space-y-1 relative z-10">
          <h3 className="font-bold text-sm break-words leading-tight text-left select-none">
            {itemName}
          </h3>
          {rapidInsert && (
            <p className="text-xs font-bold select-none">
              {item.quantity} {item.unit}
            </p>
          )}
        </div>

        <TagDisplay
          attributes={itemAttributes}
          className="mt-1 text-right relative z-20"
        />
        {showModal && photoUrl && !imageError &&
          createPortal(
            // Anteprima a schermo intero simile a quella di PhotoUploader
            <div
              data-testid="photo-preview-overlay"
              role="dialog"
              aria-modal="true"
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-[5000]"
              onPointerDown={closePreview}
            >
              <img
                src={photoUrl}
                alt="Foto prodotto"
                className="max-w-[90vw] max-h-[90vh] rounded shadow-lg"
              />
            </div>,
            document.body
          )}
      </div>
    );
  }
);

ShoppingListItem.displayName = "ShoppingListItem";

export default React.memo(ShoppingListItem);
